import {computed, inject, Injectable, Signal, signal} from '@angular/core';
import { Router } from '@angular/router';
import { ISidenav } from '@tecalisdev/infinity';
import {finalize, forkJoin, map, Observable, of} from 'rxjs';
import { environment } from 'src/environments/environment';
import { ApiService } from './api.service';
import {BackofficeRoute, FrontofficeRoute, IUser, IUserBanner, IUserInfo, IUserResponseInfo, IUserResponseInfoMenu, Permission, PermissionType, TPermissionValue, UserRole} from '@core/models/user-info.model';
import {AuthnService} from '../../modules/authn/services/authn.service';
import {IResourceResponse} from '@core/models/app.model';
import {LoadingService} from '@shared/services/loading.service';
import {MasterService} from './master.service';
import {ErrorService} from './error.service';
import {ThemeService} from './theme.service';
import {ThemeBootstrapService} from './theme-bootstrap.service';
import {TranslateService} from '@ngx-translate/core';

/**
 * Permisos que el FE deniega si la feature del proveedor está off (CHF-649).
 * Misma lógica que el antiguo `uiRestrictions` → DENY: permiso BE ∧ feature.
 */
const FEATURE_GATED_PERMISSIONS: {feature: string; permissions: Permission[]}[] = [
	{feature: 'back.catalog', permissions: [Permission.BACK_MENU_CATALOG]},
	{
		feature: 'back.billing',
		permissions: [
			Permission.BACK_MENU_BILLING_DASHBOARD,
			Permission.BACK_MENU_BILLING_RECORDS,
			Permission.BACK_MENU_BILLING_PROFORMAS,
			Permission.BACK_MENU_BILLING_INVOICES,
			Permission.BACK_MENU_BILLING_NOTE,
			Permission.BACK_MENU_BILLING_REPORTS,
			Permission.BACK_MENU_BILLING_CONFIG,
		],
	},
	{feature: 'back.control.reports', permissions: [Permission.BACK_MENU_REPORTS]},
	{feature: 'back.control.locks', permissions: [Permission.BACK_MENU_ADMINISTRATION_LOCKS]},
	{feature: 'back.structure.menu_links', permissions: [Permission.BACK_MENU_ADMINISTRATION_MENU_LINKS]},
	{
		feature: 'back.business_manager',
		permissions: [
			Permission.BACK_MENU_BUSINESS_MANAGER,
			Permission.BACK_MENU_BUSINESS_MANAGER_FOLLOW_UP,
		],
	},
	{
		feature: 'back.business_manager.simulator',
		permissions: [Permission.BACK_MENU_BUSINESS_MANAGER_SIMULATOR],
	},
	{
		feature: 'ui.pdv_performance',
		permissions: [Permission.BACK_GET_STRUCTURE_ELEMENT_PERFORMANCE],
	},
	{
		feature: 'ui.pdv_planned_visit',
		permissions: [Permission.BACK_UPDATE_STRUCTURE_ELEMENT_PLANNED_VISIT],
	},
	{
		feature: 'ui.pdv_visits',
		permissions: [Permission.BACK_CREATE_STRUCTURE_ELEMENT_VISIT],
	},
	{
		feature: 'ui.pdv_sale_quality',
		permissions: [Permission.BACK_GET_STRUCTURE_ELEMENT_SALE_QUALITY],
	},
	{
		feature: 'ui.pdv_connectivity',
		permissions: [Permission.FRONT_UPDATE_STRUCTURE_ELEMENT_CONNECTIVITY],
	},
	{
		feature: 'ui.pending_structure_elements',
		permissions: [Permission.BACK_LIST_PENDING_STRUCTURE_ELEMENTS],
	},
	{
		feature: 'back.operations.after_sales',
		permissions: [Permission.BACK_MENU_OPERATIONS_AFTER_SALES],
	},
	{
		feature: 'back.resources',
		permissions: [Permission.BACK_MENU_RESOURCES],
	},
	{
		feature: 'front.commissions',
		permissions: [Permission.FRONT_MENU_COMMISSIONS],
	},
	{
		feature: 'back.paydex.billing',
		permissions: [Permission.BACK_MENU_PAYDEX_BILLING],
	},
];

@Injectable({
	providedIn: 'root',
})
export class UserInfoService extends ApiService {
	private authn = inject(AuthnService);
	private router = inject(Router);
	private loadingService = inject(LoadingService);
	private masterService = inject(MasterService);
	private errorService = inject(ErrorService);
	private themeService = inject(ThemeService);
	private themeBootstrap = inject(ThemeBootstrapService);
	private trans = inject(TranslateService);

	private _user = signal<IUserInfo>(null);
	private _permissions = computed(() => this._user()?.permissions || null);
	/** Permisos tal cual vienen de `/user/info` (antes de gates por feature). */
	private _apiPermissions: Record<string, TPermissionValue> | undefined;
	/** URL absoluta permitida por id de enlace externo (whitelist anti open-redirect). */
	private _externalMenuUrls = new Map<number, string>();

	public isImpersonating = computed(() => !!this._user()?.user?.is_impersonated);

	public resolveExternalMenuUrl(id: number): string | null {
		return this._externalMenuUrls.get(id) ?? null;
	}

	public static externalMenuPath(id: number): string {
		return `/go/external/${id}`;
	}

	public getUserInfoService(): Observable<IResourceResponse<IUserResponseInfo>> {
		return this.http.get<IResourceResponse<IUserResponseInfo>>(environment.apiUrl + `/user/info`, { headers: this.getHeaders() });
	}

	public setUser(value: IUserInfo): void {
		this._user.set(value);
		if (!value) {
			this._apiPermissions = undefined;
			this._externalMenuUrls.clear();
			this.themeService.reset();
			this.themeBootstrap.reapplyGlobal();
			return;
		}
		// 2º apply: tema resuelto (structure_element | global | default) + logo/spinner
		this.themeBootstrap.applyResolved(value.theme ?? null);
	}

	/** Recarga /user/info y reaplica tema (p. ej. tras CRUD de branding). */
	public refreshUserInfo(): Observable<IUserInfo> {
		return this.getUserInfoService().pipe(
			map((response) => {
				const info = this.buildUserInfo(response.data);
				this.setUser(info);
				return info;
			}),
		);
	}

	public get user(): Signal<IUserInfo> {
		return this._user;
	}

	public getUserData(): IUser {
		return this._user()?.user;
	}

	public setUserInfo() {
		this.loadingService.showMaster(this.trans.instant('LOADING.USER'));
		this.getUserInfoService().subscribe({
			next: (response) => {
				this.setUser(this.buildUserInfo(response.data));
			},
			error: () => {
				this.failSessionAndGoToLogin();
			},
			complete: () => {
				this.loadingService.hideMaster();
				if (!this.permissionAccess(Permission.FRONT_ACCESS) && !this.permissionAccess(Permission.BACK_ACCESS)) {
					this.failSessionAndGoToLogin();
					return;
				}
				this.redirectUser();
			},
		});
	}

	/**
	 * Corta el bootstrap de sesión (p. ej. /user/info con CORS/status 0 o 401) y va a login
	 * con aviso. Evita reintentos que re-disparen el mismo GET en bucle.
	 */
	public failSessionAndGoToLogin(messageKey = 'AUTHN.LOGIN.SESSION_LOAD_ERROR'): void {
		this.loadingService.hide();
		this.loadingService.hideMaster();
		this.errorService.hideGeneralError();
		this.setUser(null);
		this.authn.logout(false);
		// Clave i18n (no texto): el login traduce al montar, cuando el loader ya está listo.
		this.router.navigate(['/login'], {state: {loginErrorKey: messageKey}});
	}

	public getUserRole() {
		return this.user()?.user?.role;
	}

	/** CHF-756: CTA Solicitar apertura — solo O2 (admin, jefe comercial, mayorista, TC). */
	public canRequestStoreOpening(): boolean {
		if (environment.provider !== 'o2') {
			return false;
		}
		const role = this.getUserRole();
		const allowed =
			role === UserRole.ADMIN
			|| role === UserRole.TEAM_LEAD
			|| role === UserRole.DISTRIBUTOR
			|| role === UserRole.TECHNICAL_SALES;
		return allowed && this.permissionAccess(Permission.BACK_CREATE_STRUCTURE_ELEMENT);
	}

	/**
	 * Ruta create tienda (nivel hoja). Leer desde un `computed` para reaccionar a user/master.
	 */
	public getRequestStoreOpeningRoute(): string[] | null {
		this.user();
		this.masterService.master();
		if (!this.canRequestStoreOpening()) {
			return null;
		}
		const leafId = this.masterService.getLowestStructureLevelId();
		return leafId
			? [BackofficeRoute.STRUCTURE_ELEMENTS, leafId, 'form']
			: null;
	}

	public redirectUser() {
		this.isBackofficeUser()
			? this.router.navigate([BackofficeRoute.ROOT])
			: this.router.navigate([FrontofficeRoute.ROOT]);
	}

	// El acceso lo decide el permiso, no una lista de roles: asi un rol nuevo
	// entra al back office con solo darle back_access en el seeder.
	public isBackofficeUser(): boolean {
		return this.permissionAccess(Permission.BACK_ACCESS);
	}


	public getUserBanners(): IUserBanner[] | undefined {
		return this._user()?.banners;
	}

	public permissionAccess(permission: string): boolean {
		return this._permissions()?.[permission] === PermissionType.ALLOW;
	}

	public transformerUserMenu(response: IUserResponseInfoMenu[]): ISidenav[] {
		return response.map((row) => {
			const option: ISidenav = {
				icon: row.icon,
				label: row.text,
				title: row.title,
				divider: row.divider,
			};
			if (row.url) {
				option.url = this.resolveMenuItemUrl(row);
			}
			if (row.children?.length > 0) {
				option.menu = row.children.map((child) => ({
					icon: child.icon,
					label: child.text,
					url: child.url ? this.resolveMenuItemUrl(child) : undefined,
					divider: child.divider,
				}));
			}
			return option;
		});
	}

	private resolveMenuItemUrl(row: IUserResponseInfoMenu): string {
		if (this.isExternalMenuItem(row) && row.id != null) {
			this._externalMenuUrls.set(row.id, row.url);
			return UserInfoService.externalMenuPath(row.id);
		}
		return row.url.startsWith('/') ? row.url : `/${row.url}`;
	}

	private isExternalMenuItem(row: IUserResponseInfoMenu): boolean {
		return row.external === true || /^https?:\/\//i.test(row.url || '');
	}

	/**
	 * Menú: lo envía el BE ya filtrado por features (`MenuGenerator`).
	 * Permisos: BE ∧ feature del proveedor (deniega si feature off; mismos gates que el antiguo uiRestrictions).
	 */
	buildUserInfo(data: IUserResponseInfo): IUserInfo {
		this._apiPermissions = data.permissions ? {...data.permissions} : undefined;
		this._externalMenuUrls.clear();
		return {
			user: data.user,
			permissions: this.applyFeaturePermissionGates(this._apiPermissions),
			banners: data.banners,
			theme: data.theme,
			menu: {
				back: this.transformerUserMenu(data.menu?.back || []),
				front: this.transformerUserMenu(data.menu?.front || []),
			},
		};
	}

	/**
	 * Reaplica DENY por feature tras cargar master (p. ej. user hidratado antes en login).
	 */
	reapplyFeaturePermissionGates(): void {
		const current = this._user();
		if (!current || !this._apiPermissions) {
			return;
		}
		this._user.set({
			...current,
			permissions: this.applyFeaturePermissionGates(this._apiPermissions),
		});
	}

	/**
	 * Si master aún no está, no toca permisos (evitar DENY masivo en login).
	 * Si master está y feature missing/false → DENY de los permisos asociados.
	 */
	private applyFeaturePermissionGates(
		permissions: Record<string, TPermissionValue> | undefined,
	): Record<string, TPermissionValue> | undefined {
		if (!permissions) {
			return permissions;
		}
		if (!this.masterService.master()) {
			return permissions;
		}
		const next = {...permissions};
		for (const {feature, permissions: gated} of FEATURE_GATED_PERMISSIONS) {
			if (this.masterService.hasFeature(feature)) {
				continue;
			}
			for (const permission of gated) {
				next[permission] = PermissionType.DENY;
			}
		}
		return next;
	}

	public loadMasterAndUser(onError: () => void): void {
		this.loadingService.showMaster(this.trans.instant('LOADING.INIT'));

		const userAlreadyLoaded = !!(this.user()?.user && this.user()?.theme);

		forkJoin({
			master: this.masterService.getMaster(),
			user: userAlreadyLoaded ? of(null) : this.getUserInfoService(),
		}).pipe(
			finalize(() => this.loadingService.hideMaster())
		).subscribe({
			next: ({master, user}) => {
				this.masterService.setMaster({...master.data});
				if (user?.data) {
					this.setUser(this.buildUserInfo(user.data));
				} else if (userAlreadyLoaded) {
					this.reapplyFeaturePermissionGates();
				}
			},
			error: (err) => {
				// Bootstrap sin user hidratado + CORS/red (status 0) o 401 → login (no Reintentar,
				// que re-dispararía /user/info). Si el user ya estaba cargado, solo master falló: retry OK.
				const status = err?.status;
				if (status === 401 || (!userAlreadyLoaded && status === 0)) {
					this.failSessionAndGoToLogin();
					return;
				}
				this.errorService.showGeneralError(onError);
			},
		});
	}
}
